const activeControllers = new Map<string, AbortController>();

/**
 * Signals aborted as a "detach" — stop sending locally, but keep the S3 multipart upload and its
 * pending record intact for a later resume — rather than as a true cancel. The upload engine
 * consults this to decide whether to run the S3 abort.
 *
 * A WeakSet keyed by the signal keeps the marker attached to the abort itself, so it cannot go
 * stale or leak once the controller is collected.
 */
const detachedSignals = new WeakSet<AbortSignal>();

/**
 * Registers an in-flight upload's AbortController so a global event (logout) or a per-task action
 * (the floating widget's cancel button) can stop it.
 * @param taskId - Stable id for this upload task.
 * @param controller - The AbortController driving the upload.
 * @returns A cleanup function that unregisters the controller.
 */
export function registerActiveUpload(taskId: string, controller: AbortController): () => void {
  activeControllers.set(taskId, controller);
  return () => {
    activeControllers.delete(taskId);
  };
}

/**
 * Aborts a single in-flight upload by task id. This is a true cancel: the engine aborts the S3
 * multipart upload and the pending record is cleared, so the upload cannot be resumed.
 * @param taskId - The task to cancel.
 * @returns void
 */
export function abortUpload(taskId: string): void {
  const controller = activeControllers.get(taskId);
  if (!controller) return;
  try {
    controller.abort();
  } finally {
    activeControllers.delete(taskId);
  }
}

/**
 * Detaches every in-flight upload: stops the browser sending further parts, but marks each signal
 * so the engine does NOT abort the S3 upload or clear the pending record. Used on logout, so a
 * partially-uploaded file survives for the user to resume after logging back in.
 * @returns void
 */
export function detachActiveUploads(): void {
  activeControllers.forEach((controller) => {
    try {
      detachedSignals.add(controller.signal);
      controller.abort();
    } catch {
      /* controller already settled */
    }
  });
  activeControllers.clear();
}

/**
 * Whether a signal was aborted via detach (keep-for-resume) rather than a true cancel. The engine
 * uses this to skip the S3 multipart abort.
 * @param signal - The upload's abort signal.
 * @returns True when the abort was a detach.
 */
export function isDetachedSignal(signal: AbortSignal): boolean {
  return detachedSignals.has(signal);
}

/**
 * Whether any upload is currently registered as in-flight.
 * @returns True when at least one upload is active.
 */
export function hasActiveUploads(): boolean {
  return activeControllers.size > 0;
}

/**
 * Task ids of every upload currently in-flight in this tab. Used by the cross-tab coordinator so
 * other tabs can tell a live upload apart from an abandoned one and not show it as failed.
 * @returns Array of active task ids.
 */
export function getActiveUploadTaskIds(): string[] {
  return Array.from(activeControllers.keys());
}
